<template>
    <div>
        <v-container fluid class="pt-0">
            <v-card class="pb-2 mb-1">
                <v-card-title>{{ $t("SwissLLMSettings") }}
                    <div class="ml-auto">
                        <v-btn
                            @click="cancel()"
                            class="mr-4 mt-2"
                            color="secundary"
                        >
                            {{ $t("Cancel") }}
                        </v-btn>
                        <v-btn
                            @click="saveSettings()"
                            color="primary"
                            class="mt-2"
                        >
                            <v-icon left>mdi-content-save</v-icon>
                            {{ $t("Save") }}
                        </v-btn>
                    </div>
                </v-card-title>
                <v-card-text class="pb-2">
                    <v-row justify="center" align="center" v-if="loading">
                        <div class="text-center">
                            <div>
                                <v-progress-circular class="my-4" :size="50" width="5" color="primary"
                                    indeterminate></v-progress-circular>
                            </div>
                            <p class="display-1">{{ $t("Loading") }}...</p>
                        </div>
                    </v-row>
                    <v-row v-if="!loading">
                        <v-col cols="1"></v-col>
                        <v-col cols="2" class="pb-0">
                            <p class="ma-0 pb-0">{{ $t("UseOnPrompt") }}</p>
                        </v-col>
                        <v-col cols="3" class="pb-0">
                            <p class="ma-0 pb-0">{{ $t("AdditionalNotes") }}</p>
                        </v-col>
                        <v-col cols="5">
                        </v-col>
                    </v-row>
                    <v-row v-if="!loading" class="mb-0 pb-0">
                        <v-col>
                            <div v-for="(item, i) in contentTypeFields" :key="i">
                                <content-type-fields-infomaniak-component
                                :field="item"
                                :bidirectional-error="bidirectionalErrors[item.Code]"
                                @addToPrompt="addToTextArea"
                                @removeFromPrompt="removeFromTextArea"
                                @addAdditionalNotes="addAdditionalNotesToTextArea"
                                :refresh="refresh">
                                </content-type-fields-infomaniak-component>
                            </div>
                        </v-col>
                        <v-col>
                            <v-card class="mb-2 elevation-2 mt-0">
                                <v-card-title class="subtitle-2 font-weight-medium grey--text text--darken-1 py-2">
                                    <v-chip x-small color="primary" class="mr-2">1</v-chip>
                                    <v-icon left color="grey darken-0" small>mdi-message-text-outline</v-icon>
                                    <span class="text-body-2">{{ $t("Site.SwissLLMConfiguration.InitialContext") }}</span>
                                    <v-tooltip top max-width="300">
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-icon
                                                v-bind="attrs"
                                                v-on="on"
                                                color="grey darken-1"
                                                class="ml-2"
                                                small
                                            >
                                                mdi-information-outline
                                            </v-icon>
                                        </template>
                                        <span>{{ $t("Site.SwissLLMConfiguration.InitialContextHelp") }}</span>
                                    </v-tooltip>
                                </v-card-title>

                                <v-card-text class="py-2">
                                    <v-textarea
                                        v-model="userContext"
                                        rows="4"
                                        dense
                                        filled
                                        hide-details="auto"
                                        @input="updateFullPromptPreview"
                                        :counter="userContextMaxLength"
                                        :maxlength="userContextMaxLength"
                                        background-color="grey lighten-5"
                                        :placeholder="$t('Site.SwissLLMConfiguration.InitialContextPlaceholder')"
                                    ></v-textarea>
                                </v-card-text>
                            </v-card>

                            <v-card class="mb-2 elevation-2">
                                <v-card-title class="subtitle-2 font-weight-medium grey--text text--darken-1 py-2">
                                    <v-chip x-small color="success" class="mr-2">2</v-chip>
                                    <v-icon left color="grey darken-0" small>mdi-format-list-bulleted</v-icon>
                                    <span class="text-body-2">{{ $t("Site.SwissLLMConfiguration.FieldsSection") }}</span>
                                    <v-chip x-small color="grey lighten-1" class="ml-2">
                                        <span style="font-size: 10px;">{{ fieldsToPrompt.length }} {{ $t("Site.SwissLLMConfiguration.Selected") }}</span>
                                    </v-chip>
                                    <v-spacer></v-spacer>
                                    <v-btn
                                        icon
                                        x-small
                                        @click="showFieldsPreview = !showFieldsPreview"
                                    >
                                        <v-icon small>{{ showFieldsPreview ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                                    </v-btn>
                                </v-card-title>

                                <v-expand-transition>
                                    <v-card-text v-show="showFieldsPreview" class="py-2">
                                        <div class="pa-2 grey lighten-5 rounded" style="max-height: 150px; overflow-y: auto;">
                                            <div v-if="fieldsToPrompt.length === 0" class="text-center grey--text py-2">
                                                <v-icon small color="grey">mdi-checkbox-blank-outline</v-icon>
                                                <p class="caption mb-0 mt-1">{{ $t("Site.SwissLLMConfiguration.NoFieldsSelected") }}</p>
                                            </div>
                                            <div v-else class="d-flex flex-wrap">
                                                <v-tooltip
                                                    v-for="(field, idx) in fieldsToPrompt"
                                                    :key="idx"
                                                    top
                                                    max-width="400"
                                                >
                                                    <template v-slot:activator="{ on, attrs }">
                                                        <v-chip
                                                            v-bind="attrs"
                                                            v-on="on"
                                                            x-small
                                                            color="blue lighten-4"
                                                            class="ma-1"
                                                        >
                                                            <v-icon x-small left>mdi-check-circle</v-icon>
                                                            {{ truncateText(extractFieldName(field.prompt), 20) }}
                                                            <v-icon v-if="field.additionalNotes" x-small right color="orange">mdi-note-text</v-icon>
                                                        </v-chip>
                                                    </template>
                                                    <div>
                                                        <div class="font-weight-bold mb-1">{{ extractFieldName(field.prompt) }}</div>
                                                        <div v-if="field.additionalNotes" class="caption">
                                                            <v-icon x-small color="orange">mdi-note-text</v-icon>
                                                            {{ field.additionalNotes }}
                                                        </div>
                                                        <div v-else class="caption grey--text">{{ $t("Site.SwissLLMConfiguration.NoAdditionalNotes") }}</div>
                                                    </div>
                                                </v-tooltip>
                                            </div>
                                        </div>
                                    </v-card-text>
                                </v-expand-transition>
                            </v-card>

                            <v-card class="mb-2 elevation-2">
                                <v-card-title class="subtitle-2 font-weight-medium grey--text text--darken-1 py-2">
                                    <v-chip x-small color="warning" class="mr-2">3</v-chip>
                                    <v-icon left color="grey darken-0" small>mdi-text-box-plus-outline</v-icon>
                                    <span class="text-body-2">{{ $t("Site.SwissLLMConfiguration.AdditionalInstructions") }}</span>
                                    <v-tooltip top max-width="300">
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-icon
                                                v-bind="attrs"
                                                v-on="on"
                                                color="grey darken-1"
                                                class="ml-2"
                                                small
                                            >
                                                mdi-information-outline
                                            </v-icon>
                                        </template>
                                        <span>{{ $t("Site.SwissLLMConfiguration.AdditionalInstructionsHelp") }}</span>
                                    </v-tooltip>
                                </v-card-title>

                                <v-card-text class="py-2">
                                   <v-textarea
                                        v-model="additionalInstructions"
                                        rows="4"
                                        dense
                                        filled
                                        hide-details="auto"
                                        @input="updateFullPromptPreview"
                                        :maxlength="additionalInstructionsMaxLength"
                                        background-color="grey lighten-5"
                                        :placeholder="$t('Site.SwissLLMConfiguration.AdditionalInstructionsPlaceholder')"
                                    ></v-textarea>
                                </v-card-text>
                            </v-card>

                            <v-card class="mb-1 elevation-2">
                                <v-card-title class="subtitle-2 font-weight-medium grey--text text--darken-1 py-2">
                                    <v-icon left color="grey darken-0" small>mdi-eye-outline</v-icon>
                                    <span class="text-body-2">{{ $t("Site.SwissLLMConfiguration.FullPromptPreview") }}</span>
                                    <v-spacer></v-spacer>
                                    <v-chip small :color="promptCharsColor">
                                        {{ fullPromptLength }} / {{ maxPromptLength }}
                                    </v-chip>
                                    <v-btn
                                        color="primary"
                                        icon
                                        @click="showFullPrompt = !showFullPrompt"
                                        class="ml-1"
                                    >
                                        <v-icon>{{ showFullPrompt ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                                    </v-btn>
                                </v-card-title>

                                <v-expand-transition>
                                    <v-card-text v-show="showFullPrompt" class="py-2">
                                        <v-textarea
                                            v-model="fullPrompt"
                                            rows="15"
                                            filled
                                            readonly
                                            hide-details="auto"
                                            background-color="grey lighten-5"
                                            :error-messages="fullPromptErrors"
                                        ></v-textarea>
                                        <div class="caption grey--text text--darken-1 mt-2 d-flex align-center">
                                            <v-icon small color="info">mdi-information</v-icon>
                                            <span class="ml-1">{{ $t("Site.SwissLLMConfiguration.PreviewOnlyEditAbove") }}</span>
                                        </div>
                                    </v-card-text>
                                </v-expand-transition>
                            </v-card>

                            <v-card class="elevation-2 mt-0">
                                <v-card-text class="pb-0">
                                    <div class="subtitle-2 font-weight-medium grey--text text--darken-0 mb-4 d-flex align-center">
                                        {{ $t("Settings") }}
                                    </div>

                                    <v-row align="center" no-gutters class="mb-5">
                                        <v-col cols="12" sm="6" md="6" class="pr-3">
                                            <div class="d-flex align-center">
                                                <v-tooltip top>
                                                    <template v-slot:activator="{ on, attrs }">
                                                        <v-icon
                                                            v-bind="attrs"
                                                            v-on="on"
                                                            color="grey darken-1"
                                                            class="mr-2 ml-1"
                                                        >
                                                            mdi-information-outline
                                                        </v-icon>
                                                    </template>
                                                    <span>{{ $t("Site.AI.FuzzyThresholdDescription") }}</span>
                                                </v-tooltip>
                                                <v-text-field
                                                    :label="$t('Site.AI.FuzzyThreshold')"
                                                    v-model="fuzzyThreshold"
                                                    type="number"
                                                    min="0"
                                                    max="100"
                                                    dense
                                                    hide-details="auto"
                                                    :error-messages="fuzzyThresholdErrors"
                                                ></v-text-field>
                                            </div>
                                        </v-col>

                                        <v-col cols="12" sm="6" md="6" class="pl-3">
                                            <llm-model-selector
                                                :value="selectedModel"
                                                @model-changed="onModelChanged"
                                            />
                                        </v-col>
                                    </v-row>

                                    <v-divider class="mb-4 mt-4"></v-divider>

                                    <div class="subtitle-2 font-weight-medium grey--text text--darken-0 mb-1 d-flex align-center">
                                        {{ $t("TestRecognition") }}
                                    </div>

                                    <v-row align="center">
                                        <v-col cols="12" md="6">
                                            <v-file-input
                                                v-model="selectedFile"
                                                truncate-length="15"
                                                dense
                                                hide-details="auto"
                                            ></v-file-input>
                                        </v-col>
                                        <v-col cols="12" md="6" class="d-flex">
                                            <v-btn
                                                color="primary"
                                                :loading="loadingModal"
                                                @click="performTest()"
                                                class="mr-2"
                                                small
                                            >
                                                <v-icon left>mdi-check</v-icon>
                                                {{ $t("TestRecognition") }}
                                            </v-btn>
                                        </v-col>
                                    </v-row>
                                </v-card-text>
                            </v-card>
                        </v-col>
                    </v-row>
                </v-card-text>
            </v-card>

        </v-container>
        <infomaniak-results-modal :loading="loadingModal" :ref="modalKey" :results="performValues"></infomaniak-results-modal>
    </div>
</template>

<script>
import ContentTypeService from '@/services/ContentTypeService';
import ContentTypeFieldsInfomaniakComponent from '@/components/administration/contentType/documentIntelligence/infomaniak/ContentTypeFieldsInfomaniakComponent.vue';
import DocumentIntelligenceProvidersService from '@/services/DocumentIntelligenceProvidersService.js';
import Utils from '@/ecm/common/Utils';
import DocumentIntelligenceProvidersSettings from '@/ecm/models/contentType/documentIntelligence/DocumentIntelligenceProvidersSettings.js';
import InfomaniakResultsModal from './InfomaniakResultsModal.vue';
import { maxLength } from 'vuelidate/lib/validators';
import { validationMixin } from 'vuelidate';
import LlmModelSelector from './LlmModelSelector.vue';
import ParameterService from '@/services/ParameterService';
import ParametersKeys from '@/ecm/variables/ParametersKeys.js';
import FieldType from '@/ecm/variables/FieldType';
import DeffieldService from '@/services/DefFieldService';

export default {
    components: {
        ContentTypeFieldsInfomaniakComponent,
        InfomaniakResultsModal,
        LlmModelSelector
    },
    async created() {
        this.loading = true;
        this.documentIntelligenceProviderSettings = this.documentIntelligenceProviderSettings.toDto();
        this.additionalInstructions = this.$t('InfomaniakPromptAdditionalInstructions');
        this.loadContentTypeConfig(this.contentTypeId);
    },
    data() {
        return {
            loading: false,
            contentTypeId: this.$route.params.contentTypeID,
            settingID: this.$route.params.settingID,
            contentTypeFields: [],
            documentIntelligenceProviderSettings: new DocumentIntelligenceProvidersSettings(),
            fieldsCodes: [],
            fieldsToPrompt: [],
            userContext: '',
            additionalInstructions: '',
            fullPrompt: '',
            jsonFormat: [],
            refresh: false,
            performValues: [],
            showResultsModal: false,
            modalKey: 'modalKey',
            selectedFile: null,
            loadingModal: false,
            selectedModel: '',
            fuzzyThreshold: 75,
            showFullPrompt: false,
            showFieldsPreview: false,
            maxPromptLength: 20000,
            bidirectionalErrors: {}
        };
    },
    mixins: [validationMixin],
    validations: {
        fullPrompt: {
            maxLength: maxLength(20000)
        },
        fuzzyThreshold: {
            min: value => value >= 0,
            max: value => value <= 100
        }
    },
    computed: {
        additionalInstructionsMaxLength() {
            const availableSpace = 20000 - (this.fullPromptLength - this.additionalInstructions.length);
            return Math.max(this.additionalInstructions.length, availableSpace);
        },
        userContextMaxLength() {
            const availableSpace = 20000 - (this.fullPromptLength - this.userContext.length);
            return Math.max(this.userContext.length, availableSpace);
        },
        modalInstance() {
            return this.$refs[this.modalKey];
        },
        fullPromptErrors() {
            const errors = [];
            if (!this.$v.fullPrompt.$dirty) return errors;
            !this.$v.fullPrompt.maxLength && errors.push(
                this.$t('Site.SwissLLMConfiguration.PromptExceedsMaxLength').replace('{max}', '20000')
            );
            return errors;
        },
        fuzzyThresholdErrors() {
            const errors = [];
            if (!this.$v.fuzzyThreshold.$dirty) return errors;
            (!this.$v.fuzzyThreshold.min || !this.$v.fuzzyThreshold.max) &&
                errors.push(this.$t('Site.AI.FuzzyThresholdRange'));
            return errors;
        },
        fullPromptLength() {
            return this.fullPrompt.length;
        },
        promptCharsColor() {
            if (this.fullPromptLength == 20000) return 'error';
            if (this.fullPromptLength > 8000) return 'warning';
            return 'success';
        }
    },
    watch: {
        fullPromptLength(newValue) {
            if (newValue >= 20000) {
                Utils.toast.warning(this.$t('Site.SwissLLMConfiguration.PromptExceedsMaxLength').replace('{max}', '20000'));
            }
        }
    },

    methods: {
        async loadContentTypeConfig(contentTypeId) {
            const data = await ContentTypeService.getFields(contentTypeId);
            if (this.settingID != 0) {
                await this.getSettingsById(this.settingID, data);
            } else {
                for (var item of data) {
                    item.Selected = false;
                    item.additionalNotes = '';
                }
                this.contentTypeFields = data;
                this.updateFullPromptPreview();
                this.loading = false;
            }
            if (!this.selectedModel) {
                const param = await ParameterService.getStringParameter(ParametersKeys.AIDefaultModel);
                this.selectedModel = param.value;
            }
        },

        extractFieldName(prompt) {
            const fieldItem = this.fieldsToPrompt.find(f => f.prompt === prompt);

            if (fieldItem && fieldItem.fieldName) {
                return fieldItem.fieldName;
            }

            if (fieldItem && fieldItem.fieldCode) {
                const field = this.contentTypeFields.find(f => f.Code === fieldItem.fieldCode);
                if (field) return field.Name;
            }

            return prompt.split(':')[0].trim();
        },
        truncateText(text, maxLength) {
            if (!text) return '';
            if (text.length <= maxLength) return text;
            return text.substring(0, maxLength) + '...';
        },

        updateFullPromptPreview() {
            const fieldsToPromptString = this.fieldsToPrompt.map(item => {
                if (item.additionalNotes && item.additionalNotes.trim().length > 0) {
                    return item.prompt + ', ' + item.additionalNotes;
                }
                return item.prompt;
            }).join('.\n').concat(this.fieldsToPrompt.length ? '.' : '');

            const jsonFormatString = this.jsonFormat.join(';').concat(this.jsonFormat.length ? ';' : '');

            const generatedPrompt = this.$t('InfomaniakPrompt')
                .replace('-*.MARKER_INITIAL_CONTEXT.*-', this.userContext ? this.userContext.trim() : '')
                .replace('-*.MARKER_CT_FIELDS.*-', fieldsToPromptString)
                .replace('-*.MARKER_JSON_KEYS.*-', jsonFormatString)
                .replace('-*.MARKER_ADDITIONAL_INSTRUCTIONS.*-', this.additionalInstructions ? this.additionalInstructions.trim() : '');

            this.fullPrompt = generatedPrompt;
        },

        async getSettingsById(settingID, contentTypeFields) {
            const data = await DocumentIntelligenceProvidersService.getDocumentIntelligenceSettings(settingID);
            this.documentIntelligenceProviderSettings = data;
            const jsonConfig = JSON.parse(this.documentIntelligenceProviderSettings.configJSON);

            for (var field of contentTypeFields) {
                const fieldInConfig = jsonConfig.fieldsConfig.find(x => x.code === field.Code);
                if (fieldInConfig) {
                    field.Selected = true;
                    field.additionalNotes = fieldInConfig.additionalNotes;
                    this.fieldsCodes.push(field.Code);
                    var promptMapped = this.$t('InfomaniakPromptField')
                        .replace('{0}', field.Name)
                        .replace('{1}', field.Type)
                        .replace('{2}', field.Code);
                    this.fieldsToPrompt.push({
                        prompt: promptMapped,
                        additionalNotes: fieldInConfig.additionalNotes,
                        fieldName: field.Name,
                        fieldCode: field.Code
                    });
                    this.jsonFormat.push(`'${field.Code}'`);
                }
            }

            this.selectedModel = data.llmModel;
            this.fuzzyThreshold = data.fuzzyThreshold;

            if (jsonConfig.userContext && jsonConfig.userContext !== undefined) {
                this.userContext = jsonConfig.userContext;
            } else {
                this.userContext = '';
            }

            if (jsonConfig.additionalInstructions && jsonConfig.additionalInstructions !== undefined) {
                this.additionalInstructions = jsonConfig.additionalInstructions;
            } else if (data.prompt) {
                this.additionalInstructions = data.prompt;
            } else {
                this.additionalInstructions = this.$t('InfomaniakPromptAdditionalInstructions');
            }

            this.contentTypeFields = contentTypeFields;
            this.updateFullPromptPreview();
            this.loading = false;
        },

        async addToTextArea(prompt, fieldCode) {
            const field = this.contentTypeFields.find(f => f.Code === fieldCode);
            this.addToPrompt(prompt, fieldCode);
            if (field && (field.FieldType === FieldType.DynamicTableList || field.FieldType === FieldType.DynamicList)) {
                await this.validateBidirectionalRelation(field);
            }
        },

        async removeFromTextArea(event, fieldCode) {
            this.removeFromPrompt(event, fieldCode);
            this.$delete(this.bidirectionalErrors, fieldCode);
            const field = this.contentTypeFields.find(f => f.Code === fieldCode);
            if (field && (field.FieldType === FieldType.DynamicTableList || field.FieldType === FieldType.DynamicList)) {
                this.revalidateAllListFields();
            }
        },
        async validateBidirectionalRelation(field) {
            const validationRequest = {
                defFieldId: field.Id,
                selectedFields: this.fieldsCodes
            };

            DeffieldService.validateBidirectionalRelation(validationRequest)
                .then((data) => {
                    if (data.hasBidirectionalRelation) {
                        this.$set(this.bidirectionalErrors, field.Code,
                                  this.$t('Site.AI.FieldHasInvalidBidirectionalRelation').replaceAll('{0}', data.relatedFieldName));
                    } else {
                        this.$delete(this.bidirectionalErrors, field.Code);
                    }
                })
                .catch(() => {
                    this.$delete(this.bidirectionalErrors, field.Code);
                });
        },
        revalidateAllListFields() {
            const selectedListFields = this.contentTypeFields.filter(f =>
                f.Selected &&
                (f.FieldType === FieldType.DynamicTableList || f.FieldType === FieldType.DynamicList)
            );

            selectedListFields.forEach(field => {
                this.validateBidirectionalRelation(field);
            });
        },

        async addAdditionalNotesToTextArea(event, additionalNotes) {
            this.addAdditionalNotes(event, additionalNotes);
        },

        addToPrompt(prompt, fieldCode) {
            const field = this.contentTypeFields.find(f => f.Code === fieldCode);

            if (!field) return;

            const simulatedFields = [
                ...this.fieldsToPrompt,
                {
                    prompt,
                    additionalNotes: '',
                    fieldName: field.Name,
                    fieldCode: fieldCode
                }
            ];

            const fieldsToPromptString = simulatedFields
                .map(item => {
                    if (item.additionalNotes && item.additionalNotes.trim().length > 0) {
                        return item.prompt + ', ' + item.additionalNotes;
                    }
                    return item.prompt;
                })
                .join('.\n')
                .concat(simulatedFields.length ? '.' : '');

            const jsonFormatString = [...this.jsonFormat, `'${fieldCode}'`]
                .join(';')
                .concat(this.jsonFormat.length ? ';' : '');

            const simulatedPrompt = this.$t('InfomaniakPrompt')
                .replace('-*.MARKER_INITIAL_CONTEXT.*-', this.userContext.trim())
                .replace('-*.MARKER_CT_FIELDS.*-', fieldsToPromptString)
                .replace('-*.MARKER_JSON_KEYS.*-', jsonFormatString)
                .replace('-*.MARKER_ADDITIONAL_INSTRUCTIONS.*-', this.additionalInstructions.trim());

            const simulatedLength = simulatedPrompt.length;

            if (simulatedLength > this.maxPromptLength) {
                Utils.toast.error(
                    this.$t('Site.SwissLLMConfiguration.CannotAddFieldLimitExceeded', {
                        current: this.fullPromptLength,
                        max: this.maxPromptLength
                    })
                );

                this.$nextTick(() => {
                    const field = this.contentTypeFields.find(f => f.Code === fieldCode);
                    if (field) field.Selected = false;
                });

                return;
            }

            this.fieldsToPrompt.push({
                prompt,
                additionalNotes: '',
                fieldName: field.Name,
                fieldCode: fieldCode
            });

            this.jsonFormat.push(`'${fieldCode}'`);
            this.fieldsCodes.push(fieldCode);
            this.updateFullPromptPreview();
        },

        removeFromPrompt(event, fieldCode) {
            const index = this.fieldsToPrompt.findIndex(linea => linea.prompt.includes(event));
            const indexJson = this.jsonFormat.findIndex(linea => linea.includes(fieldCode));
            const indexFiledsCodes = this.fieldsCodes.findIndex(field => field === fieldCode);

            if (index !== -1) {
                this.fieldsToPrompt.splice(index, 1);
            }
            if (indexJson !== -1) {
                this.jsonFormat.splice(indexJson, 1);
            }
            if (indexFiledsCodes !== -1) {
                this.fieldsCodes.splice(indexFiledsCodes, 1);
            }
            this.updateFullPromptPreview();
        },

        addAdditionalNotes(event, additionalNotes) {
            const index = this.fieldsToPrompt.findIndex(linea => linea.prompt.includes(event));
            if (index !== -1) {
                if (additionalNotes.trim() === '') {
                    this.fieldsToPrompt[index].additionalNotes = '';
                } else {
                    this.fieldsToPrompt[index].additionalNotes = additionalNotes.toLowerCase();
                }
            }
            this.updateFullPromptPreview();
        },

        async refreshData() {
            var res = await this.$confirm(this.$t('RefreshData'), {
                title: this.$t('Confirm'),
                icon: 'mdi-alert',
                color: 'warning',
                buttonTrueText: this.$t('Ok'),
                buttonFalseText: this.$t('Cancel'),
            });
            if (res == 1) {
                this.fieldsToPrompt = [];
                this.jsonFormat = [];
                this.fieldsCodes = [];
                this.userContext = '';
                this.additionalInstructions = '';
                this.refresh = true;
                this.updateFullPromptPreview();
            }
        },

        saveSettings() {
            this.$v.$touch();
            if (this.$v.$invalid) {
                return;
            }

            if (this.fullPromptLength > 20000) {
                Utils.toast.error(
                    this.$t('Site.SwissLLMConfiguration.PromptExceedsMaxLength').replace('{max}', '20000')
                );
                return;
            }

            this.loading = true;

            var fieldsConfig = [];
            for (var field of this.contentTypeFields) {
                if (!field.Selected) continue;
                fieldsConfig.push({
                    code: field.Code,
                    additionalNotes: field.additionalNotes
                });
            }

            var jsonString = JSON.stringify({
                fieldsConfig: fieldsConfig,
                language: 'en',
                userContext: this.userContext,
                additionalInstructions: this.additionalInstructions
            });

            this.documentIntelligenceProviderSettings.Id = parseInt(this.settingID);
            this.documentIntelligenceProviderSettings.ContentTypeID = parseInt(this.contentTypeId);
            this.documentIntelligenceProviderSettings.DocumentIntelligenceProviderID = 3;
            this.documentIntelligenceProviderSettings.Prompt = this.fullPrompt;
            this.documentIntelligenceProviderSettings.ConfigJSON = jsonString;
            this.documentIntelligenceProviderSettings.LLMModel = this.selectedModel;
            this.documentIntelligenceProviderSettings.FuzzyThreshold = this.fuzzyThreshold;

            DocumentIntelligenceProvidersService.SaveContentTypeDocumentIntelligenceProviderAsync(
                this.documentIntelligenceProviderSettings
            )
                .then((data) => {
                    Utils.toast.success(
                        this.$t('DocumentIntelligenceProviderSettings') + ' ' +
                            this.$t('OperationSuccess')
                    );
                    this.$router.push(
                        `/administration/content-type/document-intelligence-settings/${this.contentTypeId}`
                    );
                })
                .catch(() => {
                    this.loading = false;
                });
        },

        cancel() {
            this.$router.push({
                path: `/administration/content-type/document-intelligence-settings/${this.contentTypeId}`
            });
        },

        hideModal() {
            this.showResultsModal = false;
        },

        async performTest() {
            this.$v.$touch();
            if (this.$v.$invalid) {
                return;
            }
            if (!this.fieldsCodes || this.fieldsCodes.length === 0) {
                Utils.toast.error(this.$t('FieldsRequired'));
                return;
            }

            if (this.selectedFile) {
                this.cleanResults();
                this.loadingModal = true;

                const performTestRequest = {
                    prompt: this.fullPrompt,
                    Fields: this.contentTypeFields.filter((x) => x.Selected),
                    Base64File: await this.getBase64UploadedFile(),
                    LLMModel: this.selectedModel,
                    ContentTypeId: this.contentTypeId,
                    FuzzyThreshold: this.fuzzyThreshold
                };

                DocumentIntelligenceProvidersService.performInfomaniakLLMTest(performTestRequest)
                    .then((data) => {
                        this.performValues = data;
                        this.loadingModal = false;
                        this.modalInstance.showModal();
                    })
                    .catch(() => {
                        this.loadingModal = false;
                    });

            } else {
                Utils.toast.error(this.$t('DocumentAttachmentRequired'));
                return;
            }
        },

        async getBase64UploadedFile() {
            const base64 = Utils.file.toBase64(this.selectedFile);
            return base64;
        },

        cleanResults() {
            this.performValues = null;
        },

        onModelChanged(modelName) {
            this.selectedModel = modelName;
        }
    },
};
</script>
